import { useState, useMemo } from "react";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { Table, Input, Select, Button, Tag, Progress, Row, Col, Tooltip } from "antd";

const { Option } = Select;

export function TaskListView({ data }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState("all");
  const [filterActivity, setFilterActivity] = useState("all");
  const [filterTask, setFilterTask] = useState("all");
  const [filterIndicator, setFilterIndicator] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const dynamicActivities = useMemo(() => {
    if (filterProject === "all") return [];
    const project = data.proyectos.find((p) => p.codigo === filterProject);
    if (!project) return [];
    const actividades = [];
    project.fases.forEach((f) => {
      f.tareas.forEach((t) => {
        if (!actividades.some((a) => a.descripcion === t.actividad)) {
          actividades.push({
            descripcion: t.actividad,
            fase: f.nombre,
            faseSigla: f.sigla,
          });
        }
      });
    });
    return actividades;
  }, [data, filterProject]);

  const dynamicStatuses = useMemo(() => {
    let source = data.todasLasTareas;

    if (filterProject !== "all")
      source = source.filter((t) => t.proyectoCodigo === filterProject);
    if (filterActivity !== "all")
      source = source.filter((t) => t.actividad === filterActivity);
    if (filterTask !== "all")
      source = source.filter((t) => t.codigo === filterTask);
    if (filterIndicator !== "all")
      source = source.filter((t) =>
        t.indicadores.some((ind) => ind.indicador === filterIndicator)
      );

    const baseStates = ["sobrecumplido", "completado", "en-progreso", "en-riesgo", "atrasado"];
    const foundStates = Array.from(new Set(source.map((t) => t.estado)));
    return Array.from(new Set([...baseStates, ...foundStates]));
  }, [data, filterProject, filterActivity, filterTask, filterIndicator]);

  // 🔹 Filtrado global de tareas
  const filteredTasks = useMemo(() => {
    return data.todasLasTareas.filter((t) => {
      const matchesSearch =
        t.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.indicadores.some((ind) =>
          ind.indicador.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesProject =
        filterProject === "all" || t.proyectoCodigo === filterProject;
      const matchesActivity =
        filterActivity === "all" || t.actividad === filterActivity;
      const matchesTask = filterTask === "all" || t.codigo === filterTask;
      const matchesIndicator =
        filterIndicator === "all" ||
        t.indicadores.some((ind) => ind.indicador === filterIndicator);
      const matchesStatus =
        filterStatus === "all" || t.estado === filterStatus;

      return (
        matchesSearch &&
        matchesProject &&
        matchesActivity &&
        matchesTask &&
        matchesIndicator &&
        matchesStatus
      );
    });
  }, [
    data,
    searchTerm,
    filterProject,
    filterActivity,
    filterTask,
    filterIndicator,
    filterStatus,
  ]);

  // 🔹 Paginación
  const paginatedTasks = filteredTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 🔹 Resetear filtros
  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterProject("all");
    setFilterActivity("all");
    setFilterTask("all");
    setFilterIndicator("all");
    setFilterStatus("all");
    setCurrentPage(1);
  };

  // 🔹 Semáforo de colores actualizado con sobrecumplido 💚
  const getStatusTag = (estado) => {
    const map = {
      sobrecumplido: { color: "#21a766ff", label: "Sobrecumplido" },
      completado: { color: "green", label: "Completado" },
      "en-progreso": { color: "blue", label: "En Progreso" },
      "en-riesgo": { color: "orange", label: "En Riesgo" },
      atrasado: { color: "red", label: "Atrasado" },
    };
    const conf = map[estado] || { color: "default", label: estado };
    return <Tag color={conf.color}>{conf.label}</Tag>;
  };
  const columns = [
    {
      title: "Proyecto",
      dataIndex: "proyecto",
      width: 250,
      key: "proyecto",
      render: (_, record) => (
        <Tooltip title={`${record.proyectoCodigo} - ${record.proyecto}`}>
          {`${record.proyectoCodigo} - ${record.proyecto}`}
        </Tooltip>
      ),
    },
    {
      title: "Actividad",
      dataIndex: "actividad",
      key: "actividad",
      width: 240,
      ellipsis: { showTitle: false },
      render: (_, record) => {
        const textoCompleto = `${record.codigoActividad || "—"} - ${record.actividad}`;
        const textoCorto =
          textoCompleto.length > 45
            ? `${textoCompleto.slice(0, 45)}...`
            : textoCompleto;

        return (
          <Tooltip title={textoCompleto}>
            {textoCorto}
          </Tooltip>
        );
      },
    },

    {
      title: "Fase",
      dataIndex: "fase",
      key: "fase",
      width: 150,
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "Tarea",
      dataIndex: "descripcion",
      key: "descripcion",
      width: 240,
      ellipsis: { showTitle: false },
      render: (_, record) => {
        const textoCompleto = `${record.codigo} - ${record.descripcion}`;
        const textoCorto =
          textoCompleto.length > 45
            ? `${textoCompleto.slice(0, 45)}...`
            : textoCompleto;

        return (
          <Tooltip title={textoCompleto}>
            {textoCorto}
          </Tooltip>
        );
      },
    },

    {
      title: "Avance (%)",
      dataIndex: "porcentajePromedio",
      key: "porcentajePromedio",
      align: "center",
      width: 120,
      render: (value) => (
        <Tooltip title={`Avance: ${value.toFixed(2)}%`}>
          <Progress
            percent={Number(value.toFixed(0))}
            size="small"
            strokeColor={
              value > 100
                ? "#00E676" 
                : value === 100
                  ? "green"
                  : value >= 75
                    ? "#1890ff"
                    : value >= 25
                      ? "orange"
                      : "red"
            }
          />
        </Tooltip>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      align: "center",
      width: 140,
      render: (estado) => getStatusTag(estado),
    },
  ];

  return (
    <div>
      <Row gutter={[12, 12]} style={{ padding: 12 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Buscar tarea o indicador..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </Col>

        <Col xs={12} sm={8} md={4}>
          <Select
            value={filterProject}
            onChange={(v) => {
              setFilterProject(v);
              setFilterActivity("all");
              setFilterTask("all");
              setFilterIndicator("all");
              setFilterStatus("all");
            }}
            style={{ width: "100%" }}
            placeholder="Proyecto"
          >
            <Option value="all">Todos los proyectos</Option>
            {data.proyectos.map((p) => (
              <Option key={p.codigo} value={p.codigo}>
                {p.codigo} - {p.nombre}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={8} md={4}>
          <Select
            value={filterActivity}
            onChange={(v) => {
              setFilterActivity(v);
              setFilterTask("all");
              setFilterIndicator("all");
              setFilterStatus("all");
            }}
            style={{ width: "100%" }}
            placeholder="Actividad"
            disabled={filterProject === "all"}
          >
            <Option value="all">Todas las actividades</Option>
            {dynamicActivities.map((a) => (
              <Option key={a.descripcion} value={a.descripcion}>
                {a.descripcion.length > 40
                  ? `${a.descripcion.substring(0, 40)}...`
                  : a.descripcion}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={8} md={3}>
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: "100%" }}
            placeholder="Estado"

          >
            <Option value="all">Todos</Option>
            {dynamicStatuses.map((estado) => (
              <Option key={estado} value={estado}>
                {estado.charAt(0).toUpperCase() +
                  estado.slice(1).replace("-", " ")}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={8} md={2}>
          <Button icon={<ReloadOutlined />} onClick={handleClearFilters} block>
            Limpiar
          </Button>
        </Col>
      </Row>

      <Table
        style={{ padding: 12 }}
        columns={columns}
        dataSource={paginatedTasks}
        pagination={{
          current: currentPage,
          pageSize: itemsPerPage,
          total: filteredTasks.length,
          onChange: setCurrentPage,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onShowSizeChange: (current, size) => {
            setCurrentPage(1);
            setItemsPerPage(size);
          },
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} tareas`,
        }}
        rowKey={(record) => record.id}
        bordered
      />
    </div>
  );
}
